/**
 * CurrencyManager - Admin UI for managing currencies and exchange rates
 * Features:
 * - Add/edit custom currencies
 * - Fetch live exchange rates
 * - Set manual exchange rates
 * - Configure rate source preferences
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import {
  DollarSign, Plus, RefreshCw, Settings, Trash2, Edit,
  TrendingUp, TrendingDown, Globe, Calculator, Save,
  CheckCircle, AlertCircle, Clock, ArrowRightLeft
} from 'lucide-react';
import api from '@/lib/api';

const CurrencyManager = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('currencies');
  const [showAddCurrency, setShowAddCurrency] = useState(false);
  const [showAddRate, setShowAddRate] = useState(false);
  const [newCurrency, setNewCurrency] = useState({ code: '', name: '', symbol: '', is_active: true });
  const [newRate, setNewRate] = useState({ from_currency: 'USD', to_currency: '', rate: '' });
  const [baseCurrency, setBaseCurrency] = useState('USD');

  // Fetch currencies
  const { data: currencies = [], isLoading: loadingCurrencies } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => api.get('/admin/currencies').then(r => r.data),
  });

  // Fetch exchange rates
  const { data: ratesData, isLoading: loadingRates, refetch: refetchRates } = useQuery({
    queryKey: ['exchange-rates', baseCurrency],
    queryFn: () => api.get(`/admin/exchange-rates?base=${baseCurrency}`).then(r => r.data),
  });

  // Fetch currency settings
  const { data: currencySettings = {} } = useQuery({
    queryKey: ['currency-settings'],
    queryFn: () => api.get('/admin/currency-settings').then(r => r.data),
  });

  // Add currency mutation
  const addCurrencyMutation = useMutation({
    mutationFn: (currency) => api.post('/admin/currencies', currency),
    onSuccess: () => {
      queryClient.invalidateQueries(['currencies']);
      toast.success('Currency added');
      setShowAddCurrency(false);
      setNewCurrency({ code: '', name: '', symbol: '', is_active: true });
    },
    onError: () => toast.error('Failed to add currency')
  });

  // Update currency mutation (enable/disable/edit)
  const updateCurrencyMutation = useMutation({
    mutationFn: ({ code, data }) => api.put(`/admin/currencies/${code}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['currencies']);
      toast.success('Currency updated');
    },
    onError: (error) => toast.error(error?.response?.data?.detail || 'Failed to update currency')
  });

  // Set default currency mutation
  const setDefaultMutation = useMutation({
    mutationFn: (code) => api.post('/admin/currency/set-default', { code }),
    onSuccess: () => {
      queryClient.invalidateQueries(['currencies']);
      queryClient.invalidateQueries(['currency-settings']);
      toast.success('Default currency updated');
    },
    onError: (error) => toast.error(error?.response?.data?.detail || 'Failed to set default currency')
  });

  // Delete currency mutation
  const deleteCurrencyMutation = useMutation({
    mutationFn: (code) => api.delete(`/admin/currencies/${code}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['currencies']);
      toast.success('Currency removed');
    },
    onError: (error) => toast.error(error?.response?.data?.detail || 'Failed to remove currency')
  });

  // Set manual rate mutation
  const setRateMutation = useMutation({
    mutationFn: (rate) => api.post('/admin/exchange-rates', rate),
    onSuccess: () => {
      queryClient.invalidateQueries(['exchange-rates']);
      toast.success('Exchange rate updated');
      setShowAddRate(false);
      setNewRate({ from_currency: 'USD', to_currency: '', rate: '' });
    },
    onError: () => toast.error('Failed to update rate')
  });

  // Delete manual rate mutation
  const deleteRateMutation = useMutation({
    mutationFn: ({ from, to }) => api.delete(`/admin/exchange-rates/${from}/${to}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['exchange-rates']);
      toast.success('Manual rate removed');
    },
    onError: () => toast.error('Failed to remove rate')
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (settings) => api.put('/admin/currency-settings', settings),
    onSuccess: () => {
      queryClient.invalidateQueries(['currency-settings']);
      toast.success('Settings updated');
    },
    onError: () => toast.error('Failed to update settings')
  });

  // Fetch live rates mutation
  const fetchLiveRatesMutation = useMutation({
    mutationFn: () => api.get(`/admin/exchange-rates/live?base=${baseCurrency}`).then(r => r.data),
    onSuccess: (data) => {
      if (data.source === 'live') {
        toast.success(`Fetched ${Object.keys(data.rates || {}).length} live rates`);
        refetchRates();
      } else {
        toast.error('Failed to fetch live rates');
      }
    }
  });

  const activeCurrencies = currencies.filter(c => c.is_active);

  return (
    <div className="space-y-6" data-testid="currency-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Currency & Exchange Rates</h2>
          <p className="text-sm text-slate-500">Manage currencies and configure exchange rates</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="currencies">
            <DollarSign className="h-4 w-4 mr-2" />
            Currencies
          </TabsTrigger>
          <TabsTrigger value="rates">
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Exchange Rates
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Currencies Tab */}
        <TabsContent value="currencies" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-600">{activeCurrencies.length} active currencies</p>
            <Dialog open={showAddCurrency} onOpenChange={setShowAddCurrency}>
              <DialogTrigger asChild>
                <Button data-testid="add-currency-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Currency
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Custom Currency</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Currency Code *</Label>
                    <Input
                      value={newCurrency.code}
                      onChange={(e) => setNewCurrency({ ...newCurrency, code: e.target.value.toUpperCase() })}
                      placeholder="e.g., ZWL, XOF"
                      maxLength={5}
                    />
                    <p className="text-xs text-slate-500 mt-1">3-letter ISO code</p>
                  </div>
                  <div>
                    <Label>Currency Name *</Label>
                    <Input
                      value={newCurrency.name}
                      onChange={(e) => setNewCurrency({ ...newCurrency, name: e.target.value })}
                      placeholder="e.g., Zimbabwe Dollar"
                    />
                  </div>
                  <div>
                    <Label>Symbol *</Label>
                    <Input
                      value={newCurrency.symbol}
                      onChange={(e) => setNewCurrency({ ...newCurrency, symbol: e.target.value })}
                      placeholder="e.g., Z$, $"
                      maxLength={5}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newCurrency.is_active}
                      onCheckedChange={(v) => setNewCurrency({ ...newCurrency, is_active: v })}
                    />
                    <Label>Active</Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddCurrency(false)}>Cancel</Button>
                  <Button 
                    onClick={() => addCurrencyMutation.mutate(newCurrency)}
                    disabled={!newCurrency.code || !newCurrency.name || !newCurrency.symbol}
                  >
                    Add Currency
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {currencies.map((currency) => (
              <Card key={currency.code} className={!currency.is_active ? 'opacity-60 bg-slate-50' : ''}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center font-bold text-lg ${
                        currency.is_default ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100'
                      }`}>
                        {currency.symbol}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{currency.code}</p>
                          {currency.is_default && (
                            <Badge className="text-xs bg-emerald-100 text-emerald-700">Default</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{currency.name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {currency.is_common && (
                        <Badge variant="secondary" className="text-xs">Common</Badge>
                      )}
                      {!currency.is_active && (
                        <Badge variant="outline" className="text-xs text-red-500">Inactive</Badge>
                      )}
                      
                      {/* Toggle active state */}
                      <Switch
                        checked={currency.is_active}
                        onCheckedChange={(checked) => updateCurrencyMutation.mutate({ 
                          code: currency.code, 
                          data: { is_active: checked } 
                        })}
                        title={currency.is_active ? 'Disable currency' : 'Enable currency'}
                      />
                      
                      {/* Set as default button */}
                      {currency.is_active && !currency.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDefaultMutation.mutate(currency.code)}
                          title="Set as default"
                          className="text-emerald-600 hover:text-emerald-700"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      )}
                      
                      {/* Delete button (only for custom currencies) */}
                      {!currency.is_system && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteCurrencyMutation.mutate(currency.code)}
                          className="text-red-500 hover:text-red-600"
                          title="Delete currency"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Exchange Rates Tab */}
        <TabsContent value="rates" className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <Label className="text-xs">Base Currency</Label>
                <Select value={baseCurrency} onValueChange={setBaseCurrency}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {activeCurrencies.map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                variant="outline" 
                onClick={() => fetchLiveRatesMutation.mutate()}
                disabled={fetchLiveRatesMutation.isPending}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${fetchLiveRatesMutation.isPending ? 'animate-spin' : ''}`} />
                Fetch Live Rates
              </Button>
            </div>
            
            <Dialog open={showAddRate} onOpenChange={setShowAddRate}>
              <DialogTrigger asChild>
                <Button data-testid="add-rate-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Set Manual Rate
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Set Manual Exchange Rate</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>From Currency</Label>
                      <Select 
                        value={newRate.from_currency} 
                        onValueChange={(v) => setNewRate({ ...newRate, from_currency: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {activeCurrencies.map(c => (
                            <SelectItem key={c.code} value={c.code}>{c.code} - {c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>To Currency</Label>
                      <Select 
                        value={newRate.to_currency} 
                        onValueChange={(v) => setNewRate({ ...newRate, to_currency: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                        <SelectContent>
                          {activeCurrencies.filter(c => c.code !== newRate.from_currency).map(c => (
                            <SelectItem key={c.code} value={c.code}>{c.code} - {c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Exchange Rate</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">1 {newRate.from_currency} =</span>
                      <Input
                        type="number"
                        step="0.0001"
                        value={newRate.rate}
                        onChange={(e) => setNewRate({ ...newRate, rate: e.target.value })}
                        placeholder="0.00"
                        className="w-32"
                      />
                      <span className="text-sm">{newRate.to_currency || '???'}</span>
                    </div>
                  </div>
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Manual rates override live rates. Delete the manual rate to revert to live rates.
                    </AlertDescription>
                  </Alert>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddRate(false)}>Cancel</Button>
                  <Button 
                    onClick={() => setRateMutation.mutate({
                      from_currency: newRate.from_currency,
                      to_currency: newRate.to_currency,
                      rate: parseFloat(newRate.rate),
                      is_manual: true
                    })}
                    disabled={!newRate.to_currency || !newRate.rate}
                  >
                    Save Rate
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Rate source info */}
          {ratesData && (
            <Alert className={ratesData.source === 'live' ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}>
              {ratesData.source === 'live' ? (
                <Globe className="h-4 w-4 text-green-600" />
              ) : (
                <Clock className="h-4 w-4 text-amber-600" />
              )}
              <AlertDescription>
                {ratesData.source === 'live' ? (
                  <span>Live rates from <strong>{ratesData.api_source}</strong> • Updated: {new Date(ratesData.last_updated).toLocaleString()}</span>
                ) : (
                  <span>Using manual/cached rates</span>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Rates table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Exchange Rates (1 {baseCurrency} =)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {ratesData?.rates && Object.entries(ratesData.rates).map(([code, rate]) => {
                  const isManual = ratesData.manual_rates?.[code];
                  return (
                    <div 
                      key={code} 
                      className={`p-3 rounded-lg border ${isManual ? 'border-blue-200 bg-blue-50' : 'bg-slate-50'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{code}</span>
                        {isManual && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteRateMutation.mutate({ from: baseCurrency, to: code })}
                            className="h-6 w-6 p-0 text-red-500"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                      <p className="text-lg font-bold">{typeof rate === 'number' ? rate.toFixed(4) : rate}</p>
                      {isManual && (
                        <Badge variant="outline" className="text-xs mt-1">Manual</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
              {(!ratesData?.rates || Object.keys(ratesData.rates).length === 0) && (
                <p className="text-center text-slate-500 py-8">No exchange rates available. Click "Fetch Live Rates" to get current rates.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Currency Settings</CardTitle>
              <CardDescription>Configure how currencies and exchange rates are handled</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Use Live Exchange Rates</Label>
                  <p className="text-sm text-slate-500">Automatically fetch rates from online sources</p>
                </div>
                <Switch
                  checked={currencySettings.use_live_rates ?? true}
                  onCheckedChange={(v) => updateSettingsMutation.mutate({ ...currencySettings, use_live_rates: v })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Show Currency Conversion</Label>
                  <p className="text-sm text-slate-500">Display converted prices to attendees</p>
                </div>
                <Switch
                  checked={currencySettings.show_conversion ?? true}
                  onCheckedChange={(v) => updateSettingsMutation.mutate({ ...currencySettings, show_conversion: v })}
                />
              </div>
              
              <div>
                <Label className="font-medium">Default Currency</Label>
                <Select 
                  value={currencySettings.default_currency || 'USD'}
                  onValueChange={(v) => updateSettingsMutation.mutate({ ...currencySettings, default_currency: v })}
                >
                  <SelectTrigger className="w-48 mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {activeCurrencies.map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.code} - {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Alert>
                <Calculator className="h-4 w-4" />
                <AlertDescription>
                  <strong>How rates work:</strong> Manual rates always override live rates. 
                  If no manual rate is set and live rates are enabled, the system will fetch 
                  current rates from free exchange rate APIs.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CurrencyManager;
